/**
 * PATH: apps/quote/src/pages/claims/claim-editor-page/ClaimLinesWorkspace.tsx
 * FILE_ID: QUOTE-CLAIMS-G10-LINES-001
 * PURPOSE: Responsive, multi-mode progress valuation workspace.
 * WHY: Desktop tables and touch-friendly mobile cards share one behavior contract.
 */
import { Copy, Eraser, Search } from 'lucide-react'
import { formatCurrency } from '@/lib/utils'
import type { ClaimLineUpdate } from '@quotech/api/g10ProgressClaims'
import type { PaymentClaimLine } from './types'
import type { ClaimEditorController } from './useClaimEditorController'

interface Props {
  controller: ClaimEditorController
}

function NumberField({ label, value, onChange, max }: {
  label: string
  value: number | undefined
  onChange: (value: number) => void
  max?: number
}) {
  return (
    <label className="block min-w-24 text-xs text-slate-500">
      {label}
      <input
        type="number"
        min={0}
        max={max}
        step="0.01"
        value={value ?? ''}
        onChange={(event) => onChange(Number(event.target.value))}
        className="mt-1 min-h-10 w-full rounded-md border border-slate-300 bg-white px-2 py-1.5 text-right font-mono text-sm dark:border-slate-600 dark:bg-slate-900"
      />
    </label>
  )
}

function ValuationInputs({ line, value, update }: {
  line: PaymentClaimLine
  value: ClaimLineUpdate
  update: (patch: Partial<ClaimLineUpdate>) => void
}) {
  if (value.valuation_mode === 'percentage') {
    return <NumberField label="This claim %" value={value.this_claim_percent} max={100 - Number(line.previous_percent)} onChange={(this_claim_percent) => update({ this_claim_percent })} />
  }
  if (value.valuation_mode === 'measured_quantity') {
    return <NumberField label="This claim quantity" value={value.this_claim_quantity} onChange={(this_claim_quantity) => update({ this_claim_quantity })} />
  }
  if (value.valuation_mode === 'milestone') {
    const complete = (value.this_claim_amount ?? 0) > 0
    return (
      <label className="flex min-h-11 items-center gap-2 text-sm">
        <input type="checkbox" checked={complete} onChange={(event) => update({ this_claim_amount: event.target.checked ? Number(line.value) - Number(line.previous_amount) : 0 })} />
        Milestone achieved
      </label>
    )
  }
  if (value.valuation_mode === 'daywork') {
    return <NumberField label="This claim hours" value={value.this_claim_quantity} onChange={(this_claim_quantity) => update({ this_claim_quantity })} />
  }
  return null
}

function LineEditor({ line, controller, compact = false }: {
  line: PaymentClaimLine
  controller: ClaimEditorController
  compact?: boolean
}) {
  const value = controller.lineValue(line)
  const update = (patch: Partial<ClaimLineUpdate>) => controller.updateLine(line, patch)
  return (
    <div className={compact ? 'space-y-3' : 'grid grid-cols-[minmax(12rem,1fr)_10rem_minmax(13rem,1fr)] items-center gap-3'}>
      <div className="min-w-0">
        <p className="font-medium text-slate-900 dark:text-white">{line.description}</p>
        <p className="text-xs text-slate-500">{line.location || line.line_type} · {formatCurrency(Number(line.value))} · previous {Number(line.previous_percent).toFixed(1)}%</p>
      </div>
      <div className="text-xs text-slate-500">
        Valuation mode
        <span className="mt-1 block min-h-10 rounded-md border border-slate-200 bg-slate-50 px-2 py-2 text-sm capitalize dark:border-slate-700 dark:bg-slate-900">
          {value.valuation_mode.replace('_', ' ')}
        </span>
      </div>
      <ValuationInputs line={line} value={value} update={update} />
    </div>
  )
}

export function ClaimLinesWorkspace({ controller }: Props) {
  return (
    <section aria-label="Claim line valuation" className="space-y-4">
      <div className="flex flex-wrap gap-2 rounded-xl border border-slate-200 bg-white p-3 dark:border-slate-700 dark:bg-slate-800">
        <label className="relative min-w-56 flex-1">
          <span className="sr-only">Search claim lines</span>
          <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-400" />
          <input value={controller.search} onChange={(event) => controller.setSearch(event.target.value)} placeholder="Search location or description" className="min-h-11 w-full rounded-lg border border-slate-300 pl-9 pr-3 text-sm dark:border-slate-600 dark:bg-slate-900" />
        </label>
        <select aria-label="Filter line section" value={controller.section} onChange={(event) => controller.setSection(event.target.value as typeof controller.section)} className="min-h-11 rounded-lg border border-slate-300 bg-white px-3 text-sm dark:border-slate-600 dark:bg-slate-900">
          <option value="all">All sections</option><option value="contract">Contract</option><option value="variation">Variations</option>
        </select>
        <button type="button" onClick={() => controller.bulkPercent(10)} className="min-h-11 rounded-lg border px-3 text-sm hover:bg-slate-50 dark:hover:bg-slate-700">Set visible 10%</button>
        <button type="button" onClick={controller.copyPrior} className="inline-flex min-h-11 items-center gap-1 rounded-lg border px-3 text-sm"><Copy className="h-4 w-4" />Copy prior</button>
        <button type="button" onClick={controller.clearCurrent} className="inline-flex min-h-11 items-center gap-1 rounded-lg border px-3 text-sm"><Eraser className="h-4 w-4" />Clear current</button>
      </div>

      {controller.validation.length > 0 && (
        <div role="alert" className="rounded-lg border border-red-200 bg-red-50 p-3 text-sm text-red-700">
          <p className="font-medium">Resolve valuation errors before submitting.</p>
          {controller.validation.map((error) => <p key={error}>• {error}</p>)}
        </div>
      )}

      {controller.visibleLines.length === 0 ? (
        <div className="rounded-xl border border-dashed p-10 text-center text-sm text-slate-500">No lines match the current search and section.</div>
      ) : (
        <>
          <div className="hidden overflow-hidden rounded-xl border border-slate-200 bg-white sm:block dark:border-slate-700 dark:bg-slate-800">
            {controller.visibleLines.map((line) => <div key={line.id} className="border-b p-3 last:border-0 dark:border-slate-700"><LineEditor line={line} controller={controller} /></div>)}
          </div>
          <div className="space-y-3 sm:hidden">
            {controller.visibleLines.map((line) => <article key={line.id} className="rounded-xl border border-slate-200 bg-white p-4 dark:border-slate-700 dark:bg-slate-800"><LineEditor line={line} controller={controller} compact /></article>)}
          </div>
        </>
      )}
    </section>
  )
}
